import { ChevronDown, ChevronRight, Mail } from "lucide-react";
import { useSidePanel } from "@/components/contexts/SidePanelContext";
import { MailAttachmentIcon } from "@/components/ui/icons";
import { Button } from "@/components/ui/NewButton";
import { renderFileIcon } from "@/lib/document-icons";
import { cleanFileName } from "@/utils/file-utils";
import { useBundleTree, useBundleTreeActions } from "../bundleTreeStore";
import { type FileNode, getEmailFileGuid } from "../bundleTypes";

type ViewEmailGroupProps = {
  groupId: string;
};

export const ViewEmailGroup = ({ groupId }: ViewEmailGroupProps) => {
  const config = useBundleTree((s) => s.config);
  const isExpanded = useBundleTree((s) => s.expandedIds.has(groupId));
  const { toggleExpand: onToggleExpand } = useBundleTreeActions();
  const group = config.groups[groupId];
  const childIds = group?.children || [];
  const emailFileGuid = getEmailFileGuid(config, groupId);

  // childIds holds every attachment (included + unincluded); view mode shows only the included ones.
  const includedAttachmentFiles = childIds
    .slice(1)
    .map(id => config.files[id])
    .filter((f): f is NonNullable<typeof f> => !!f && f.includedInBundle !== false);
  const hasIncludedAttachments = includedAttachmentFiles.length > 0;
  const { openDocumentPanel, isOpen, activeDocumentGuid } = useSidePanel();
  const isEmailActive = isOpen && !!emailFileGuid && activeDocumentGuid === emailFileGuid;

  const EmailIcon = hasIncludedAttachments ? MailAttachmentIcon : Mail;

  return (
    <div>
      <div className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border text-sm transition-colors ${isEmailActive ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'}`}>
        {hasIncludedAttachments && (
          <Button
            icon={isExpanded ? ChevronDown : ChevronRight}
            variant="bare"
            size="icon-md"
            onClick={() => onToggleExpand(groupId)}
            className="text-gray-500 hover:text-gray-700"
          />
        )}
        <span
          className="shrink-0 cursor-pointer"
          onClick={() => emailFileGuid && openDocumentPanel({ documentGuid: emailFileGuid })}
        >
          <EmailIcon className="h-4 w-4 text-gray-500" />
        </span>
        <span
          className="text-sm font-medium flex-1 truncate cursor-pointer hover:text-blue-600 transition-colors"
          onClick={() => emailFileGuid && openDocumentPanel({ documentGuid: emailFileGuid })}
        >
          {group.title}
          {hasIncludedAttachments && (
            <span className="text-xs text-gray-400 font-normal ml-2">
              {includedAttachmentFiles.length} attachment{includedAttachmentFiles.length !== 1 ? 's' : ''}
            </span>
          )}
        </span>
      </div>
      {isExpanded && hasIncludedAttachments && (
        <div className="ml-[18px] pl-3 border-l border-gray-200">
          {includedAttachmentFiles.map((file) => (
            <ViewEmailAttachment key={file.fileId} file={file} />
          ))}
        </div>
      )}
    </div>
  );
};

type ViewEmailAttachmentProps = {
  file: FileNode;
};

const ViewEmailAttachment = ({ file }: ViewEmailAttachmentProps) => {
  const { openDocumentPanel, isOpen, activeDocumentGuid } = useSidePanel();
  const isActive = isOpen && activeDocumentGuid === file.fileReferenceGuid;
  const displayName = file.customName || cleanFileName(file.fileName);
  const open = () => openDocumentPanel({ documentGuid: file.fileReferenceGuid });

  return (
    <div className={`flex items-center gap-2 px-2 py-1 rounded-sm text-sm transition-colors ${isActive ? 'bg-blue-50 border border-blue-300' : 'hover:bg-gray-50'}`}>
      <div className="shrink-0 cursor-pointer" onClick={open}>
        {renderFileIcon(`file${file.fileExtension}`, 'h-4 w-4')}
      </div>
      <span
        className="flex-1 truncate text-sm cursor-pointer hover:text-blue-600 transition-colors"
        onClick={open}
      >
        {displayName}
      </span>
    </div>
  );
};
